@if (!canAccessSupportAdmin()) {
  <div class="mx-auto max-w-3xl px-4 py-16 text-center">
    <h1 class="text-xl font-semibold text-primary">Access denied</h1>
    <p class="mt-2 text-sm text-secondary">
      Support admin is only available to SHOUT agency staff.
    </p>
  </div>
} @else {
  <div class="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h1 class="text-2xl font-semibold text-primary">Support dashboard</h1>
        <p class="mt-1 text-sm text-secondary">Manage incoming support tickets.</p>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <button type="button" (click)="openOutbound()" class="btn-basic-sm btn-primary">
          New ticket
        </button>
        <button type="button" (click)="openTemplates()" class="btn-basic-sm btn-secondary">
          Templates
        </button>
        <button type="button" (click)="openSettings()" class="btn-basic-sm btn-secondary">
          Settings
        </button>
      </div>
    </div>

    <div class="mt-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <p class="m-0 text-sm font-medium text-primary">Ops stats</p>
        <div
          class="inline-flex rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 p-0.5 text-sm shadow-sm"
          role="group"
          aria-label="Stats time window">
          @for (option of statsWindowOptions; track option.value) {
            <button
              type="button"
              (click)="onStatsWindowChange(option.value)"
              [class]="statsWindowButtonClass(option.value)"
              [attr.aria-pressed]="statsWindow === option.value">
              {{ option.label }}
            </button>
          }
        </div>
      </div>

      <div class="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
        @if (statsLoading()) {
          @for (row of [0, 1, 2, 3, 4, 5, 6]; track row) {
            <div class="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-3">
              <div class="h-3 w-24 animate-pulse rounded bg-gray-100 dark:bg-zinc-700"></div>
              <div class="mt-3 h-7 w-16 animate-pulse rounded bg-gray-200 dark:bg-zinc-600"></div>
            </div>
          }
        } @else {
          <button
            type="button"
            [class]="directionTileClass('inbound')"
            [attr.aria-pressed]="directionFilter() === 'inbound'"
            (click)="toggleDirectionFilter('inbound')">
            <p class="text-xs font-medium uppercase tracking-wide text-secondary">Inbound tickets</p>
            <p class="mt-1 text-2xl font-semibold text-primary">{{ stats()?.inboundTickets ?? 0 }}</p>
          </button>
          <button
            type="button"
            [class]="directionTileClass('outbound')"
            [attr.aria-pressed]="directionFilter() === 'outbound'"
            (click)="toggleDirectionFilter('outbound')">
            <p class="text-xs font-medium uppercase tracking-wide text-secondary">Outbound tickets</p>
            <p class="mt-1 text-2xl font-semibold text-primary">{{ stats()?.outboundTickets ?? 0 }}</p>
          </button>
          <div class="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-3">
            <p class="text-xs font-medium uppercase tracking-wide text-secondary">Avg time to close</p>
            <p class="mt-1 text-2xl font-semibold text-primary">{{ formatDuration(stats()?.avgTimeToCloseMs) }}</p>
          </div>
          <div class="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-3">
            <p class="text-xs font-medium uppercase tracking-wide text-secondary">Avg age (open)</p>
            <p class="mt-1 text-2xl font-semibold text-primary">{{ formatDuration(stats()?.avgOpenAgeMs) }}</p>
          </div>
          <div class="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-3">
            <p class="text-xs font-medium uppercase tracking-wide text-secondary">Avg first response</p>
            <p class="mt-1 text-2xl font-semibold text-primary">{{ formatDuration(stats()?.avgFirstResponseMs) }}</p>
          </div>
          <div class="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-3">
            <p class="text-xs font-medium uppercase tracking-wide text-secondary">
              Avg first response (Mon-Fri 9-5)
            </p>
            <p class="mt-1 text-2xl font-semibold text-primary">
              {{ formatDuration(stats()?.avgFirstResponseBusinessHoursMs) }}
            </p>
          </div>
          <div class="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-3">
            <p class="text-xs font-medium uppercase tracking-wide text-secondary">Reopened</p>
            <p class="mt-1 text-2xl font-semibold text-primary">{{ formatReopenRate(stats()?.reopenRate) }}</p>
          </div>
        }
      </div>

      <p class="mt-2 text-xs text-secondary">
        Averages and the reopened rate cover inbound tickets only, so they measure how we answer
        people rather than how often we start a conversation.
      </p>
    </div>

    <div class="mt-6 flex flex-wrap gap-3">
      <select
        [(ngModel)]="statusFilter"
        (ngModelChange)="onFiltersChange()"
        class="rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-primary">
        <option value="open">Open</option>
        <option value="closed">Closed</option>
        <option value="all">All statuses</option>
      </select>

      <select
        [(ngModel)]="assignedFilter"
        (ngModelChange)="onFiltersChange()"
        class="min-w-48 rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 py-2 pl-3 pr-8 text-sm text-primary"
        aria-label="Assigned to">
        <option value="all">All assignments</option>
        <option value="mine">Assigned to me</option>
        <option value="unassigned">Unassigned</option>
        @for (admin of teammateAdmins(); track admin.userId) {
          <option [ngValue]="admin.userId">{{ adminLabel(admin) }}</option>
        }
      </select>

      <select
        [(ngModel)]="regionFilter"
        (ngModelChange)="onFiltersChange()"
        class="rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-primary"
        aria-label="Region">
        @for (option of regionFilterOptions; track option.value) {
          <option [ngValue]="option.value">{{ option.label }}</option>
        }
      </select>

      <input
        type="search"
        [(ngModel)]="search"
        (keyup.enter)="onFiltersChange()"
        placeholder="Search name, ticket #, subject, or user ID"
        aria-label="Search tickets by name, ticket number, subject, or user ID"
        class="min-w-56 flex-1 rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-primary" />
      <button type="button" (click)="onFiltersChange()" class="btn-basic-sm btn-primary">Search</button>

      @if (directionFilter(); as direction) {
        <button
          type="button"
          (click)="toggleDirectionFilter(direction)"
          class="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-950 dark:text-indigo-200">
          {{ direction === 'outbound' ? 'Outbound only' : 'Inbound only' }}
          <span aria-hidden="true">&times;</span>
          <span class="sr-only">Clear the {{ direction }} filter</span>
        </button>
      }
    </div>

    @if (loading()) {
      <div
        class="mt-8 overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900"
        aria-busy="true"
        aria-label="Loading tickets">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-zinc-700 text-xs">
          <thead class="bg-gray-50 dark:bg-zinc-800">
            <tr>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Ticket</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Type</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">User</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Account</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Alerted by</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Status</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Assigned to</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Last reply</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Last activity</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-zinc-700">
            @for (row of skeletonRows; track row) {
              <tr>
                <td class="px-4 py-3">
                  <div class="h-4 w-40 max-w-full animate-pulse rounded bg-gray-200 dark:bg-zinc-700"></div>
                  <div class="mt-2 h-3 w-16 animate-pulse rounded bg-gray-100 dark:bg-zinc-800"></div>
                </td>
                <td class="px-4 py-3"><div class="h-4 w-24 animate-pulse rounded bg-gray-100 dark:bg-zinc-800"></div></td>
                <td class="px-4 py-3"><div class="h-4 w-28 animate-pulse rounded bg-gray-100 dark:bg-zinc-800"></div></td>
                <td class="px-4 py-3"><div class="h-4 w-16 animate-pulse rounded bg-gray-100 dark:bg-zinc-800"></div></td>
                <td class="px-4 py-3"><div class="h-4 w-20 animate-pulse rounded bg-gray-100 dark:bg-zinc-800"></div></td>
                <td class="px-4 py-3"><div class="h-5 w-14 animate-pulse rounded-full bg-gray-100 dark:bg-zinc-800"></div></td>
                <td class="px-4 py-3"><div class="h-4 w-32 animate-pulse rounded bg-gray-100 dark:bg-zinc-800"></div></td>
                <td class="px-4 py-3"><div class="h-5 w-16 animate-pulse rounded-full bg-gray-100 dark:bg-zinc-800"></div></td>
                <td class="px-4 py-3"><div class="h-4 w-24 animate-pulse rounded bg-gray-100 dark:bg-zinc-800"></div></td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else if (tickets().length === 0) {
      <p class="mt-8 text-sm text-secondary">No tickets match your filters.</p>
    } @else {
      <div class="mt-8 overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-zinc-700 text-xs">
          <thead class="bg-gray-50 dark:bg-zinc-800">
            <tr>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Ticket</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Type</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">User</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Account</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Alerted by</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Status</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Assigned to</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Last reply</th>
              <th class="px-4 py-3 text-left font-semibold text-secondary">Last activity</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-zinc-700">
            @for (ticket of tickets(); track ticket.id) {
              <tr
                class="cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800"
                (click)="openTicket(ticket.id)">
                <td class="px-4 py-3">
                  <p class="font-medium text-primary">{{ ticket.subject }}</p>
                  <p class="text-xs text-secondary">#{{ ticket.ticketNumber }}</p>
                </td>
                <td class="px-4 py-3">
                  <span
                    class="rounded-full px-2 py-0.5 text-xs font-medium"
                    [class]="ticketDirectionClass(ticket)">
                    {{ ticketDirection(ticket) }}
                  </span>
                  <p class="mt-1 text-primary">{{ categoryLabel(ticket.category) }}</p>
                </td>
                <td class="px-4 py-3 text-primary">
                  @if (countryFlagForTicket(ticket); as flag) {
                    <span class="inline-flex items-center gap-1.5">
                      <span class="text-base leading-none" aria-hidden="true" [title]="flag.label">{{
                        flag.emoji
                      }}</span>
                      <p class="m-0">{{ ticket.user?.fullName || '—' }}</p>
                      <span class="sr-only">{{ flag.label }}</span>
                    </span>
                  } @else {
                    {{ ticket.user?.fullName || '—' }}
                  }
                </td>
                <td class="px-4 py-3 text-primary">{{ formatUserType(ticket.user?.userType) }}</td>
                <td class="px-4 py-3 text-primary">{{ formatAlertPreferences(ticket.alertPreferences) }}</td>
                <td class="px-4 py-3">
                  <span class="rounded-full px-2 py-0.5 text-xs font-medium" [class]="statusClass(ticket.status)">
                    {{ ticket.status }}
                  </span>
                </td>
                <td class="px-4 py-3 text-primary">{{ formatAssignedAdminName(ticket.assignedAdmin) }}</td>
                <td class="px-4 py-3">
                  <span
                    class="rounded-full px-2 py-0.5 text-xs font-medium"
                    [class]="lastReplyClass(ticket.lastMessageAuthorType)">
                    {{ lastReplyLabel(ticket.lastMessageAuthorType) }}
                  </span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-secondary">
                  {{ formatDateTime(ticket.lastMessageAt) }}
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div class="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p class="m-0 text-sm text-secondary" aria-live="polite">{{ pageRangeLabel() }}</p>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="btn-basic-sm btn-secondary"
            [disabled]="!canGoPrevious()"
            (click)="goToPreviousPage()">
            Previous
          </button>
          <button
            type="button"
            class="btn-basic-sm btn-secondary"
            [disabled]="!canGoNext()"
            (click)="goToNextPage()">
            Next
          </button>
        </div>
      </div>
    }
  </div>

  <app-support-outbound-ticket
    [open]="showOutbound()"
    [admins]="admins"
    [templates]="templateMessages"
    (closed)="closeOutbound()"
    (created)="onOutboundCreated($event)" />

  <app-dialog [open]="showSettings()" (closed)="closeSettings()">
    <div dialog-header>
      <h2 class="text-lg font-semibold text-primary">Support settings</h2>
    </div>
    <div dialog-body class="flex flex-col gap-4 text-left">
      @if (settingsLoading()) {
        <div class="space-y-4" aria-busy="true" aria-label="Loading settings">
          @for (row of settingsSkeletonRows; track row) {
            <div class="space-y-2">
              <div class="h-4 w-40 animate-pulse rounded bg-gray-200 dark:bg-zinc-700"></div>
              <div class="h-10 w-full animate-pulse rounded-md bg-gray-100 dark:bg-zinc-800"></div>
            </div>
          }
        </div>
      } @else {
        <div>
          <p class="text-sm font-medium text-primary">Assign tickets automatically to</p>
          <p class="mt-1 text-xs text-secondary">
            Choose an admin for each ticket type. Leave unset to skip auto-assign for that type.
          </p>
          <div class="mt-4 space-y-4">
            @for (option of categoryOptions; track option.value) {
              <div>
                <label [for]="'auto-assign-' + option.value" class="block text-sm font-medium text-primary">
                  {{ option.label }}
                </label>
                <select
                  [id]="'auto-assign-' + option.value"
                  [(ngModel)]="autoAssignByCategory[option.value]"
                  class="mt-2 w-full min-w-0 rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 py-2 pl-3 pr-8 text-sm text-primary">
                  <option [ngValue]="null">Do not auto-assign</option>
                  @for (admin of admins; track admin.userId) {
                    <option [ngValue]="admin.userId">{{ adminLabel(admin) }}</option>
                  }
                </select>
              </div>
            }
          </div>
        </div>

        <div class="border-t border-gray-100 dark:border-zinc-800 pt-4">
          <p class="text-sm font-medium text-primary">Auto-close &amp; notifications</p>
          <p class="mt-1 text-xs text-secondary">
            Control inactivity auto-close and when users are notified about closed tickets.
          </p>

          <label class="mt-4 flex items-start gap-3 text-sm text-secondary">
            <input type="checkbox" class="mt-1 rounded border-gray-300" [(ngModel)]="autoCloseEnabled" />
            <span>
              <span class="font-medium text-primary">Auto-close inactive tickets</span>
              <span class="mt-0.5 block text-xs text-secondary">
                Close open tickets when support last replied and the user has not responded.
              </span>
            </span>
          </label>

          <div class="mt-4">
            <label for="auto-close-days" class="block text-sm font-medium text-primary">
              Days until auto-close
            </label>
            <input
              id="auto-close-days"
              type="number"
              min="1"
              max="365"
              [(ngModel)]="autoCloseAfterDays"
              [disabled]="!autoCloseEnabled"
              class="mt-2 w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-primary disabled:bg-gray-50 disabled:text-secondary" />
          </div>

          <label class="mt-4 flex items-start gap-3 text-sm text-secondary">
            <input type="checkbox" class="mt-1 rounded border-gray-300" [(ngModel)]="notifyUserOnAdminClose" />
            <span class="font-medium text-primary">Notify user when admin closes a ticket</span>
          </label>

          <label class="mt-3 flex items-start gap-3 text-sm text-secondary">
            <input
              type="checkbox"
              class="mt-1 rounded border-gray-300"
              [(ngModel)]="notifyUserOnAutoClose"
              [disabled]="!autoCloseEnabled" />
            <span class="font-medium text-primary">Notify user when a ticket is auto-closed</span>
          </label>

          <div class="mt-4">
            <label for="admin-reply-delay" class="block text-sm font-medium text-primary">
              Admin reply notify delay (minutes)
            </label>
            <p class="mt-1 text-xs text-secondary">
              Wait this long after an admin reply before notifying the user (0 = immediate).
            </p>
            <input
              id="admin-reply-delay"
              type="number"
              min="0"
              max="60"
              [(ngModel)]="adminReplyNotifyDelayMinutes"
              class="mt-2 w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-primary" />
          </div>

          <label class="mt-4 flex items-start gap-3 text-sm text-secondary">
            <input type="checkbox" class="mt-1 rounded border-gray-300" [(ngModel)]="skipDelayForFirstResponse" />
            <span>
              <span class="font-medium text-primary">Notify instantly on a ticket's first response</span>
              <span class="mt-0.5 block text-xs text-secondary">
                Skip the delay above for a ticket's very first admin reply (10 seconds) so users hear
                back fast, even if the delay above is set higher.
              </span>
            </span>
          </label>
        </div>
      }

      @if (settingsError()) {
        <p class="text-sm text-red-600">{{ settingsError() }}</p>
      }

      <div class="mt-2 flex justify-end gap-2 border-t border-gray-100 dark:border-zinc-800 pt-3">
        <button type="button" class="btn-basic-sm btn-secondary" (click)="closeSettings()">Cancel</button>
        <button
          type="button"
          class="btn-basic-sm btn-primary disabled:opacity-50"
          [disabled]="settingsLoading() || settingsSaving()"
          (click)="saveSettings()">
          {{ settingsSaving() ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </app-dialog>

  <app-dialog [open]="showTemplates()" (closed)="closeTemplates()">
    <div dialog-header>
      <h2 class="text-lg font-semibold text-primary">Template messages</h2>
    </div>
    <div dialog-body class="flex flex-col gap-4 text-left">
      @if (templatesLoading()) {
        <div class="space-y-4" aria-busy="true" aria-label="Loading templates">
          @for (row of templatesSkeletonRows; track row) {
            <div class="space-y-2 rounded-lg border border-gray-200 dark:border-zinc-700 p-3">
              <div class="h-4 w-24 animate-pulse rounded bg-gray-200 dark:bg-zinc-700"></div>
              <div class="h-10 w-full animate-pulse rounded-md bg-gray-100 dark:bg-zinc-800"></div>
              <div class="h-20 w-full animate-pulse rounded-md bg-gray-100 dark:bg-zinc-800"></div>
            </div>
          }
        </div>
      } @else {
        <p class="text-sm text-secondary">
          Saved replies support can insert or send quickly when replying to a ticket.
        </p>

        <div class="space-y-4">
          @for (template of templateMessages; track template.id; let i = $index) {
            <div class="rounded-lg border border-gray-200 dark:border-zinc-700 p-3">
              <div class="flex items-start justify-between gap-2">
                <label [for]="'template-label-' + template.id" class="block text-xs font-medium text-primary">
                  Label <span class="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  (click)="removeTemplateMessage(i)"
                  class="text-xs font-medium text-red-600 hover:text-red-700">
                  Remove
                </button>
              </div>
              <input
                [id]="'template-label-' + template.id"
                type="text"
                [(ngModel)]="template.label"
                placeholder="e.g. Refund follow-up"
                class="mt-1 w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-primary" />
              <div class="mt-3 flex flex-wrap items-center justify-between gap-2">
                <label [for]="'template-body-' + template.id" class="block text-xs font-medium text-primary">
                  Message <span class="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  (mousedown)="preventDefault($event)"
                  (click)="insertFirstNameToken(template, bodyInput)"
                  class="inline-flex items-center rounded-full border border-gray-300 dark:border-zinc-600 bg-gray-50 dark:bg-zinc-800 px-2.5 py-1 text-xs font-medium text-primary hover:bg-gray-100 dark:hover:bg-zinc-700">
                  + First name
                </button>
              </div>
              <textarea
                #bodyInput
                [id]="'template-body-' + template.id"
                rows="3"
                [(ngModel)]="template.body"
                placeholder="Hi there, thanks for reaching out..."
                class="mt-1 w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-primary"></textarea>
              @if (template.body.trim()) {
                <p class="mt-2 text-xs text-secondary">
                  Preview:
                  <span class="text-primary">{{ previewTemplateBody(template.body) }}</span>
                </p>
              }
            </div>
          }
        </div>

        <button type="button" (click)="addTemplateMessage()" class="btn-basic-sm btn-secondary">
          Add template
        </button>
      }

      @if (templatesError()) {
        <p class="text-sm text-red-600">{{ templatesError() }}</p>
      }

      <div class="mt-2 flex justify-end gap-2 border-t border-gray-100 dark:border-zinc-800 pt-3">
        <button type="button" class="btn-basic-sm btn-secondary" (click)="closeTemplates()">Cancel</button>
        <button
          type="button"
          class="btn-basic-sm btn-primary disabled:opacity-50"
          [disabled]="templatesLoading() || templatesSaving()"
          (click)="saveTemplates()">
          {{ templatesSaving() ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </app-dialog>
}
